<style scoped lang="less">
@import "/lib/Colors.less";
.verification-form-group {
    .success {
        &,
        &:before,
        & > :before {
            color: @success-dark;
        }
    }
    .danger {
        &,
        &:before,
        & > :before {
            color: @danger-dark;
        }
    }
    .warning {
        &,
        &:before,
        & > :before {
            color: darken(@forge-orange, 25%);
        }
    }
    .card {
        .card-text {
            font-size: 13px;
        }
    }
    ::v-deep(.loader) {
        .icon.icon-attention {
            color: #fff !important;
        }
    }
}
</style>
<template>
    <div class="verification-form-group form-group clearfix" :class="[settings.isChild ? 'form-group-child' : null, colClass]">
        <div v-if="adminReview">
            <table class="table table-bordered mb-0">
                <thead>
                    <th colspan="2" class="p-2 border-0">
                        <button @click.prevent.stop="toggleVisibility" class="btn btn-bare text-left w-100 p-0">
                            <strong class="display-label d-flex justify-content-between align-items-center">
                                Verification Summary
                                <span class="icon" :class="[visibility ? 'icon-caret-up' : 'icon-caret-down']"></span>
                            </strong>
                        </button>
                    </th>
                </thead>
                <tbody v-if="visibility">
                    <template v-if="data.isPersonalIdVerification">
                        <tr>
                            <td>
                                <strong>CVI Score</strong>
                                <HelpIcon text="Comprehensive Verification Index. Combines the NAP & NAS score to assess." />
                            </td>
                            <td>{{ verificationResponse?.PersonalVerificationSummary.CviScore }}</td>
                        </tr>
                        <tr>
                            <td>
                                <strong>NAP Score</strong>
                                <HelpIcon text="Score based on name, address and phone." />
                            </td>
                            <td>{{ verificationResponse?.PersonalVerificationSummary.NapScore }}</td>
                        </tr>
                        <tr>
                            <td>
                                <strong>NAS Score</strong>
                                <HelpIcon text="Score based on name, address and SSN." />
                            </td>
                            <td>{{ verificationResponse?.PersonalVerificationSummary.NasScore }}</td>
                        </tr>
                        <tr v-if="(verificationResponse?.PersonalVerificationSummary.RiskIndicators ?? []).length > 0">
                            <td><strong>Risk Indicators</strong></td>
                            <td>
                                <ul class="list-unstyled mb-0">
                                    <li v-for="data of verificationResponse?.PersonalVerificationSummary.RiskIndicators" :class="[index === verificationResponse?.PersonalVerificationSummary.RiskIndicators.length - 1 ? 'mb-0' : 'mb-2']">
                                        <span class="icon icon-info warning align-text-bottom" role="img"></span>
                                        {{ data }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr v-if="verificationResponse?.DocumentVerificationSummary.PersonData != null">
                            <td><strong>Person Data</strong></td>
                            <td>
                                <dl class="row mb-0">
                                    <div class="d-flex w-100" v-for="(data, key) of verificationResponse?.DocumentVerificationSummary.PersonData">
                                        <dt class="col-auto">{{ key }}:</dt>
                                        <dd class="flex-grow-1">{{ data ?? "&mdash;" }}</dd>
                                    </div>
                                </dl>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Document Type</strong></td>
                            <td>{{ documentType(verificationResponse?.DocumentVerificationSummary.DocumentType) }}</td>
                        </tr>
                        <tr v-if="(verificationResponse?.DocumentVerificationSummary.VerificationAlerts ?? []).length > 0">
                            <td><strong>Verification Alerts</strong></td>
                            <td>
                                <ul class="list-unstyled mb-0">
                                    <li v-for="(data, index) of verificationResponse?.DocumentVerificationSummary.VerificationAlerts" :class="[index === verificationResponse?.DocumentVerificationSummary.VerificationAlerts.length - 1 ? 'mb-0' : 'mb-2']">
                                        <span class="icon align-text-bottom" :class="[styles(data.Value)['icon'], styles(data.Value)['color']]" role="img"></span>
                                        {{ data.Detail }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <!-- <button class="passthrough icon icon-edit mr-1" type="button" v-if="showAdminEditControls" @click.prevent="startAdminEdit"><span class="sr-only">Edit</span></button> -->
        </div>
        <template v-else>
            <div class="card">
                <div class="card-header">ID Verification</div>
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                        <em :id="errorId" class="card-text">
                            <div class="status ml-1" :class="styles(status)['color']" v-if="calculatedElementsValid && edited">
                                <span class="icon align-middle" :class="styles(status)['icon']" role="img" aria-hidden="true"></span>
                                {{ message }}
                            </div>
                            <ErrorList v-else :data="data" />
                        </em>
                        <button type="button" class="btn btn-primary" @click.prevent="verifyIdentity" :disabled="hasVerified" :aria-describedby="errorIds">
                            Verify
                            <AsyncOperationStatus class="loader" :promise="loadingPromise" hide-check-icon />
                        </button>
                    </div>
                </div>
            </div>
        </template>
        <Comments :data="data" />
        <AdminSettingsDifferences :data="data" />
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { usePageStore } from "../pageState/store";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import { useObservable } from "@vueuse/rxjs";
import type IdVerificationDef from "../elements/IdVerificationDef";
import type { IdVerificationSettings } from "@cs-ts/id-verification-settings";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { VerificationStatus } from "@cs/VerificationStatus";
import { DocumentTypeEnum } from "@cs/DocumentTypeEnum";

// TODO: Set customizable message mapped to every statuses
// Index 0 = InstantID, Index 1 = TrueID
const statusesMapping: Record<number, string[]> = {
    [VerificationStatus.Passed]: ["ID validated.", "ID document validated."],
    [VerificationStatus.Failed]: ["Unable to validate ID.", "Unable to validate ID document."],
    [VerificationStatus.Unknown]: ["Unknown", "A validation was prevented, reason not provided."],
    [VerificationStatus.Attention]: ["Attention", "Document appears Authentic, but some values may have been incorrectly interpreted."],
    [VerificationStatus.Skipped]: ["Skipped", "ID documentation validation skipped."],
    [VerificationStatus.Paused]: ["Paused", "Paused"],
    [VerificationStatus.Pending]: ["Pending", "Pending"],
    [VerificationStatus.Error]: ["Error", "Error"],
};
const documentMapping: Record<number, string> = {
    [DocumentTypeEnum.DriversLicense]: "Driver's License",
    [DocumentTypeEnum.Passport]: "Passport",
};
export default defineComponent({
    components: {
        ...components,
        AsyncOperationStatus,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<IdVerificationDef, IdVerificationSettings>) {
        const base = setup<IdVerificationDef, IdVerificationSettings>(props);

        const pageStore = usePageStore();

        const valid = useObservable(props.data.valid$());
        const calculatedElementsValid = useObservable(props.data.calculatedElementsValid$());
        const hasVerified = useObservable(props.data.verified$());
        const edited = useObservable(props.data.edited$());

        const loadingPromise = ref<Promise<void> | null>(null);
        const visibility = ref<boolean>(true);

        const adminReview = computed(() => pageStore.adminReview);
        const verificationResponse = computed(() => props.data.verificationResponseRaw?.Result);
        const status = computed(() => verificationResponse.value?.VerificationStatus);
        const message = computed(() => statusesMapping[status.value ?? 0][base.settings.value.type!] ?? "");

        const verifyIdentity = () => {
            if (hasVerified.value) return;
            loadingPromise.value = (async () => {
                await props.data.verifyIdentity();
            })();
        };
        const documentType = (type: number) => documentMapping[type];
        const styles = (status: number | string) => {
            let icon, color, alert: string;
            switch (status) {
                case VerificationStatus.Passed:
                case "Passed":
                    icon = "icon-check";
                    color = "success";
                    alert = "alert-success";
                    break;
                case VerificationStatus.Attention:
                case "Attention":
                    icon = "icon-attention";
                    color = "warning";
                    alert = "alert-warning";
                    break;
                case VerificationStatus.Unknown:
                case VerificationStatus.Error:
                case VerificationStatus.Failed:
                case VerificationStatus.Paused:
                case VerificationStatus.Pending:
                case VerificationStatus.Skipped:
                case "Unknown":
                case "Error":
                case "Failed":
                case "Paused":
                case "Pending":
                case "Skipped":
                    icon = "icon-attention";
                    color = "danger";
                    alert = "alert-danger";
                    break;
                default:
                    icon = "";
                    color = "";
                    alert = "";
                    break;
            }
            return { icon, color, alert };
        };
        const toggleVisibility = () => {
            visibility.value = !visibility.value;
        };

        return {
            valid,
            calculatedElementsValid,
            hasVerified,
            edited,

            loadingPromise,
            visibility,

            adminReview,
            status,
            message,
            verificationResponse,

            verifyIdentity,
            documentType,
            styles,
            toggleVisibility,

            ...base.expose,
        };
    },
});
</script>
